import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ApiContext } from '../../App';
import { useContext } from 'react';
import { toast } from 'react-toastify';
import { Camera, Trash2 } from 'lucide-react';
import { MdOutlineDesignServices } from 'react-icons/md';

const AccountInformation = () => {
    const { userData, getUserData, loading } = useAuth();
    const API = useContext(ApiContext);
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [isEditingName, setIsEditingName] = useState(false);
    const [editedName, setEditedName] = useState('');
    const [updatingName, setUpdatingName] = useState(false);

    const handleFileSelect = async (event) => {
      const file = event.target.files[0];
      if (!file) return;

      // Vérifier le type de fichier
      if (!file.type.startsWith('image/')) {
        toast.error('Seules les images sont autorisées');
        return;
      }

      // Vérifier la taille (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('La taille de l\'image ne doit pas dépasser 5MB');
        return;
      }

      // Upload vers le backend
      await uploadPhoto(file);
    };

    const updateName = async () => {
      if (!editedName.trim()) {
        toast.error('Le nom ne peut pas être vide');
        return;
      }

      setUpdatingName(true);
      try {
        const response = await API.put('/api/user/username', { name: editedName });

        if (response.data.success) {
          toast.success('Nom mis à jour avec succès');
          await getUserData();
          setIsEditingName(false);
        }
      } catch (error) {
        console.error('Error updating name', error);
        toast.error(error.response?.data?.error || 'Erreur lors de la mise à jour du nom');
      } finally {
        setUpdatingName(false);
      }
    }

    const uploadPhoto = async (file) => {
      setUploading(true);
      const formData = new FormData();
      formData.append('photo', file);

      try {
        const response = await API.post('/api/user/photo', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        if (response.data.success) {
          toast.success('Photo de profil mise à jour avec succès');
          // Rafraîchir les données utilisateur, ce qui déclenchera fetchPhotoUrl via useEffect
          await getUserData();
        }
      } catch (error) {
        console.error('Error uploading photo:', error);
        toast.error(error.response?.data?.error || 'Erreur lors de l\'upload de la photo');
        // En cas d'erreur, fetchPhotoUrl sera appelé via useEffect
      } finally {
        setUploading(false);
      }
    };

    const handleDeletePhoto = async () => {
      if (!window.confirm('Êtes-vous sûr de vouloir supprimer votre photo de profil ?')) {
        return;
      }

      try {
        const response = await API.delete('/api/user/photo');
        if (response.data.success) {
          toast.success('Photo de profil supprimée avec succès');
          // Rafraîchir les données utilisateur
          await getUserData();
        }
      } catch (error) {
        console.error('Error deleting photo:', error);
        toast.error('Erreur lors de la suppression de la photo');
      }
    };


  return (
    <div className="bg-gray-100 min-h-screen px-2 sm:px-0">

      <div className='space-y-4 sm:space-y-6 bg-white p-4 sm:p-6 rounded-lg border'>
        {/* Section Photo de profil */}
        <div className='pb-4 border-b'>
          <h2 className='text-lg sm:text-xl font-semibold mb-4'>Photo de profil</h2>
          <div className='flex flex-col sm:flex-row items-center gap-4'>
            {/* Avatar */}
            <div className='relative'>
              {loading ? (
                <div className='w-40 h-40 sm:w-24 sm:h-24 rounded-full bg-gray-200 animate-pulse border-4 border-gray-200'></div>
              ) : userData?.profilePhotoUrl ? (
                <img
                  src={userData.profilePhotoUrl}
                  alt="Profil"
                  className='w-40 h-40 sm:w-24 sm:h-24 rounded-full object-cover border-4 border-gray-200'
                />
              ) : (
                <div className='w-40 h-40 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white text-3xl sm:text-4xl font-bold border-4 border-gray-200'>
                  {userData?.name?.charAt(0).toUpperCase() || 'U'}
                </div>
              )}
              {uploading && (
                <div className='absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full'>
                  <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-white'></div>
                </div>
              )}
            </div>

            {/* Boutons d'action */}
            <div className='flex flex-col gap-2'>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className='hidden'
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className='flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm'
              >
                <Camera size={18} />
                {userData?.profilePhotoUrl ? 'Changer la photo' : 'Ajouter une photo'}
              </button>
              {userData?.profilePhotoUrl && (
                <button
                  onClick={handleDeletePhoto}
                  disabled={uploading}
                  className='flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm'
                >
                  <Trash2 size={18} />
                  Supprimer la photo
                </button>
              )}
              <p className='text-xs text-gray-500 mt-1'>
                Format accepté : JPG, PNG. Taille max : 5 MB
              </p>
            </div>
          </div>
        </div>

        {/* Informations utilisateur */}
        <div className='space-y-3 sm:space-y-4'>
          <h2 className='text-lg sm:text-xl font-semibold mb-3 sm:mb-4'>Informations du Compte</h2>
          
          <div className='flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-2 sm:gap-2'>
            <p className='text-slate-600 font-medium text-sm sm:text-base m-0 sm:pr-2'>Nom :</p>
            {isEditingName ? (
              <div className='flex items-center gap-2'>
                <input 
                  type="text"
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  className='px-3 py-1 border-1 border-gray-300 rounded-lg text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-indigo-500'
                  placeholder='Entrez votre nom'
                  disabled={updatingName}
                />
                <button
                  onClick={updateName}
                  disabled={updatingName}
                  className='px-3 py-1 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition text-sm disabled:opacity-50'
                >
                  {updatingName ? 'Sauvegarde...' : 'Sauvegarder'}
                </button>
                <button
                  onClick={() => {
                    setIsEditingName(false);
                    setEditedName('');
                  }}
                  disabled={updatingName}
                  className='px-3 py-1 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition text-sm disabled:opacity-50'
                >
                  Annuler
                </button>
              </div>
            ) : (
              <div className='flex items-center gap-2'>
                <p className='text-sm sm:text-base m-0 break-words'>{userData?.name || 'Non spécifié'}</p>
                <button
                  onClick={() => {
                    setEditedName(userData?.name || '');
                    setIsEditingName(true);
                  }}
                  className='text-indigo-600 hover:text-indigo-700 text-sm underline'
                >
                  Modifier
                </button>
              </div>
            )}
          </div>

          <div className='flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-2 sm:gap-2'>
            <p className='text-slate-600 font-medium text-sm sm:text-base m-0 sm:pr-2'>Email :</p>
            <div className='flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-2'>
              <p className='text-sm sm:text-base m-0 break-all'>{userData?.email || 'Non spécifié'}</p>
              {userData?.isAccountVerified ? (
                <span className='text-green-500 text-xs sm:text-sm'>(Vérifié)</span>
              ) : (
                <span className='text-red-500 text-xs sm:text-sm'>(Non vérifié)</span>
              )}
            </div>
          </div>

          <div className='flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-2 sm:gap-2'>
            <p className='text-slate-600 font-medium text-sm sm:text-base m-0 sm:pr-2'>Entreprise :</p>
            <p className='text-sm sm:text-base m-0 break-words'>{userData?.company || 'Non spécifié'}</p>
          </div>

          <div className='flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-2 sm:gap-2'>
            <p className='text-slate-600 font-medium text-sm sm:text-base m-0 sm:pr-2'>Membre depuis :</p>
            <p className='text-sm sm:text-base m-0'>{userData?.date ? new Date(userData.date).toLocaleDateString('fr-FR') : 'Non spécifié'}</p>
          </div>
        </div>

        {/* Section mot de passe */}
        <div className='pt-3 sm:pt-4 border-t'>
          <div className='flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0'>
            <p className='font-medium text-slate-600 text-sm sm:text-base m-0'>Mot de passe :</p>
            <button 
              className='sm:ml-2 py-2 px-4 bg-indigo-50 text-indigo-600 rounded-full hover:bg-indigo-100 transition text-sm sm:text-base self-start sm:self-auto'
              onClick={() => navigate('/reset-password')}
            >
              Réinitialiser le mot de passe
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}

export default AccountInformation